import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, ShieldCheck, AlertTriangle, CheckCircle2,
  XCircle, Lightbulb, FileText, ExternalLink, Sparkles, RefreshCw,
  Award, Target, BookOpen, Download, Link2, Check,
} from 'lucide-react';
import { Button } from './ui/button';
import useSeo from '../hooks/useSeo';
import { buildBreadcrumb, combineJsonLd } from '../seo/breadcrumbs';
import {
  buildArticleSchema,
  buildLearningResourceSchema,
  buildFaqSchema,
} from '../seo/schemas';
import { OfficialBadge } from './OfficialBadge';
import { EtmQuizFunnel } from './EtmQuizFunnel';
import { getEtmMapping } from '../data/magazine/etmMapping';
import { SafeVideoEmbed } from './SafeVideoEmbed';

/**
 * Composant générique pour les articles du Magazine Hebdomadaire CODE 2 ROUES.
 * Rend un article complet à partir d'une structure de données.
 *
 * Structure attendue (article) :
 * {
 *   slug, title, kicker, subtitle, heroImage, heroImageAlt,
 *   topRightKicker: { line1, line2 }, // optionnel
 *   badges: [{ icon, label, type: 'red'|'official' }],
 *   chapo: { kicker, body, sideStat: { kicker, big, body, accent } },
 *   pullQuote: { text, source },
 *   sections: [
 *     {
 *       part, title, intro, // string
 *       cards: [{ icon, accent, kicker, title, body }],
 *       compareCards: [{ accent, kicker, title, intro, points: [{ ok, text }] }],
 *       checkLists: [{ kicker, accent, items: [{ ok, text }] }],
 *       callouts: [{ icon, accent, body }],
 *       paragraphs: [string],
 *       orderedList: [string],
 *       normCards: [{ code, color, title, body }],
 *     }
 *   ],
 *   quiz: [{ id, question, options: [string], correctIndex, explain }],
 *   ctaFinal: { kicker, title, body, primary: {label, to}, secondary: {label, to} },
 *   source: { label, url, refs },
 *   readTimeMin: 4,
 * }
 */

// ---------- Composants d'éléments ----------
const PullQuote = ({ children, source }) => (
  <figure className="my-6 sm:my-8 border-l-4 border-l-[hsl(var(--primary))] pl-5 sm:pl-7">
    <blockquote
      className="text-lg sm:text-2xl leading-relaxed text-foreground italic"
      style={{ fontFamily: "'Crimson Text', 'Playfair Display', Georgia, serif" }}
    >
      «{' '}
      <span dangerouslySetInnerHTML={{ __html: typeof children === 'string' ? children : '' }} />
      {' '}»
    </blockquote>
    {source && (
      <figcaption className="mt-2 text-[11px] uppercase tracking-[0.16em] font-black text-foreground/55">
        — {source}
      </figcaption>
    )}
  </figure>
);

const InfoCard = ({ icon: Icon, accent = '#cf2031', kicker, title, body, testid }) => (
  <div className="mc-card p-4 sm:p-5 relative" data-testid={testid}>
    <div className="absolute top-0 left-0 bottom-0 w-1 rounded-l-2xl" style={{ background: accent }} />
    <div className="flex items-center gap-2 mb-2">
      {Icon && (
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${accent}1a`, color: accent }}>
          <Icon className="w-4 h-4" />
        </div>
      )}
      <div>
        {kicker && (
          <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: accent }}>
            {kicker}
          </div>
        )}
        <div className="font-black text-sm sm:text-base leading-tight">{title}</div>
      </div>
    </div>
    <div className="text-sm text-foreground/85 leading-relaxed" dangerouslySetInnerHTML={{ __html: body }} />
  </div>
);

const CheckRow = ({ ok = true, children }) => (
  <li className="flex items-start gap-2 text-sm text-foreground/85 leading-relaxed">
    {ok ? (
      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#16a34a] flex-shrink-0" />
    ) : (
      <XCircle className="w-4 h-4 mt-0.5 text-[#cf2031] flex-shrink-0" />
    )}
    <span dangerouslySetInnerHTML={{ __html: children }} />
  </li>
);

const ImageCompareCard = ({ card, index }) => {
  const [state, setState] = useState('bad');
  const isBad = state === 'bad';
  const img = isBad ? card.img : card.goodImg;
  const titre = isBad ? card.titre : (card.goodTitre || card.titre);
  const description = isBad ? card.description : (card.goodDescription || card.description);
  const alt = isBad ? (card.alt || card.titre) : (card.goodAlt || card.goodTitre || titre);

  return (
    <figure
      className={`group relative rounded-2xl overflow-hidden bg-slate-900/40 border transition-all cursor-pointer ${
        isBad
          ? 'border-red-500/30 hover:border-red-500/60'
          : 'border-emerald-500/40 hover:border-emerald-500/70'
      }`}
      data-testid={`imgcompare-${index}-${state}`}
    >
      <button
        type="button"
        onClick={() => setState(isBad ? 'good' : 'bad')}
        className="block w-full text-left focus:outline-none"
        aria-label={isBad ? 'Voir la bonne position' : 'Voir la mauvaise position'}
      >
        <div className="relative aspect-[3/4] overflow-hidden">
          <img
            src={img}
            alt={alt}
            key={img}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
          {/* Pastille numéro (rouge = mauvais, vert = bon) */}
          {card.numero && (
            <div
              className={`absolute top-2 left-2 w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white flex items-center justify-center shadow-lg ${
                isBad ? 'bg-red-600' : 'bg-emerald-600'
              }`}
            >
              {isBad ? (
                <span className="text-sm sm:text-base font-black text-white">{card.numero}</span>
              ) : (
                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={3} />
              )}
            </div>
          )}
          {/* Mini CTA toggle en haut-droite */}
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm text-white text-[9.5px] font-black uppercase tracking-wider shadow-lg">
            <RefreshCw className="w-3 h-3 group-hover:rotate-180 transition-transform duration-500" />
            <span className="hidden sm:inline">{isBad ? 'Bon' : 'Mauvais'}</span>
          </div>
        </div>
        <figcaption className="p-3 sm:p-4">
          <div
            className={`text-[11px] sm:text-xs font-black uppercase tracking-widest mb-1 ${
              isBad ? 'text-red-500' : 'text-emerald-500'
            }`}
          >
            {titre}
          </div>
          <p className="text-[12px] sm:text-[13px] text-foreground/80 leading-snug">
            {description}
          </p>
        </figcaption>
      </button>
    </figure>
  );
};

const CompareToggle = ({ data }) => {
  const [state, setState] = useState('good');
  const current = state === 'good' ? data.good : data.bad;
  const other = state === 'good' ? 'bad' : 'good';
  const otherLabel = state === 'good'
    ? (data.bad.label || 'Voir la mauvaise position')
    : (data.good.label || 'Voir la bonne position');
  const isGood = state === 'good';
  return (
    <figure
      className="my-4 rounded-sm overflow-hidden border-2 bg-background transition-colors"
      style={{ borderColor: isGood ? '#16a34a' : '#cf2031' }}
      data-testid="compare-toggle"
    >
      <button
        type="button"
        onClick={() => setState(other)}
        className="relative block w-full group cursor-pointer"
        aria-label={otherLabel}
        data-testid={`compare-toggle-btn-${state}`}
      >
        <img
          src={current.src}
          alt={current.alt || ''}
          loading="lazy"
          className="block w-full h-auto"
          key={current.src}
        />
        {/* Badge d'état en haut-gauche */}
        <div
          className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg text-xs font-black uppercase tracking-widest ${
            isGood ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {isGood ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
          {isGood ? 'Bonne position' : 'Mauvaise position'}
        </div>
        {/* CTA toggle en haut-droite */}
        <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm text-white text-[11px] font-bold tracking-wide shadow-lg group-hover:bg-black/90 transition">
          <RefreshCw className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform duration-500" />
          <span className="hidden sm:inline">{otherLabel}</span>
          <span className="sm:hidden">Comparer</span>
        </div>
      </button>
      {current.caption && (
        <figcaption
          className={`px-4 py-3 text-[12.5px] border-t italic ${
            isGood
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900/40 dark:text-emerald-200'
              : 'bg-red-50 border-red-200 text-red-900 dark:bg-red-950/30 dark:border-red-900/40 dark:text-red-200'
          }`}
          dangerouslySetInnerHTML={{ __html: current.caption }}
        />
      )}
    </figure>
  );
};

const Section = ({ section, idx }) => {
  return (
    <section className="space-y-4" data-reveal="" data-testid={`section-${idx}`}>
      {(section.part || section.title) && (
        <header>
          {section.part && (
            <div className="text-[10px] font-black uppercase tracking-[0.16em] text-[hsl(var(--primary))] mb-1">
              {section.part}
            </div>
          )}
          {section.title && (
            <h2
              className="text-2xl sm:text-3xl font-black tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {section.title}
            </h2>
          )}
          {section.intro && (
            <p
              className="text-base text-foreground/85 leading-relaxed mt-2"
              dangerouslySetInnerHTML={{ __html: section.intro }}
            />
          )}
        </header>
      )}

      {/* Image éditoriale inline (entre header et contenu) — utile pour
          infographies officielles, schémas, citations visuelles, etc. */}
      {section.inlineImage && (
        <figure
          className="my-4 rounded-sm overflow-hidden border border-border bg-background"
          data-testid={`article-section-image-${section.inlineImage.testid || 'inline'}`}
        >
          <img
            src={section.inlineImage.src}
            alt={section.inlineImage.alt || ''}
            loading="lazy"
            className="block w-full h-auto"
          />
          {(section.inlineImage.caption || section.inlineImage.credit) && (
            <figcaption className="px-4 py-2 text-[11px] text-muted-foreground border-t border-border bg-muted/30 flex flex-wrap items-center gap-2 justify-between">
              {section.inlineImage.caption && (
                <span
                  className="italic"
                  dangerouslySetInnerHTML={{ __html: section.inlineImage.caption }}
                />
              )}
              {section.inlineImage.credit && (
                <span className="text-[10px] uppercase tracking-wider font-semibold opacity-75">
                  © {section.inlineImage.credit}
                </span>
              )}
            </figcaption>
          )}
        </figure>
      )}

      {/* COMPARE TOGGLE — Click to toggle entre bonne et mauvaise version
          (idéal pour comparaisons avant/après pédagogiques) */}
      {section.compareToggle && (
        <CompareToggle data={section.compareToggle} />
      )}

      {/* Vidéo YouTube RGPD-safe (miniature + play → iframe nocookie) */}
      {section.videoEmbed && (
        <figure
          className="my-4"
          data-testid={`article-section-video-${section.videoEmbed.testid || 'inline'}`}
        >
          <SafeVideoEmbed
            videoId={section.videoEmbed.videoId}
            listId={section.videoEmbed.listId}
            title={section.videoEmbed.title || 'Vidéo'}
            channel={section.videoEmbed.channel || 'YouTube'}
            aspect={section.videoEmbed.aspect || '16/9'}
            testid={`article-video-${section.videoEmbed.testid || 'inline'}`}
          />
          {(section.videoEmbed.caption || section.videoEmbed.credit) && (
            <figcaption className="mt-2 text-[11px] text-muted-foreground flex flex-wrap items-center gap-2 justify-between px-1">
              {section.videoEmbed.caption && (
                <span
                  className="italic"
                  dangerouslySetInnerHTML={{ __html: section.videoEmbed.caption }}
                />
              )}
              {section.videoEmbed.credit && (
                <span className="text-[10px] uppercase tracking-wider font-semibold opacity-75">
                  © {section.videoEmbed.credit}
                </span>
              )}
            </figcaption>
          )}
        </figure>
      )}

      {section.paragraphs?.map((p, i) => (
        <p
          key={i}
          className="text-base text-foreground/85 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: p }}
        />
      ))}

      {section.orderedList && (
        <ol className="space-y-2.5 text-sm text-foreground/85 leading-relaxed list-decimal pl-5">
          {section.orderedList.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ol>
      )}

      {section.sectionImage && (
        <figure className="my-4 sm:my-5" data-testid="section-image">
          <img
            src={section.sectionImage}
            alt={section.sectionImageAlt || section.title || 'Illustration pédagogique'}
            loading="lazy"
            className="w-full rounded-xl border border-white/[0.08] shadow-2xl"
          />
          {section.sectionImageCaption && (
            <figcaption className="mt-2 text-center text-[11px] italic text-foreground/55">
              {section.sectionImageCaption}
            </figcaption>
          )}
        </figure>
      )}

      {section.cards && (
        <div className={`grid sm:grid-cols-${section.cardsCols || 2} gap-3`}>
          {section.cards.map((c, i) => (
            <InfoCard key={i} {...c} />
          ))}
        </div>
      )}

      {/* IMAGE CARDS — grille de photos avec numéro + titre + description
          + support compareCards (click pour toggle bonne/mauvaise) */}
      {section.imageCards && (
        <div
          className={`grid grid-cols-2 ${
            section.imageCardsCols === 4
              ? 'md:grid-cols-4'
              : section.imageCardsCols === 3
                ? 'md:grid-cols-3'
                : 'md:grid-cols-2'
          } gap-3 sm:gap-4`}
        >
          {section.imageCards.map((c, i) => (
            c.goodImg ? (
              <ImageCompareCard key={i} card={c} index={i} />
            ) : (
            <figure
              key={i}
              className="group relative rounded-2xl overflow-hidden bg-slate-900/40 border border-white/10 hover:border-red-500/40 transition-all"
              data-testid={`imgcard-${i}`}
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={c.img}
                  alt={c.alt || c.titre}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading="lazy"
                />
                {c.numero && (
                  <div className="absolute top-2 left-2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-600 border-2 border-white flex items-center justify-center shadow-lg">
                    <span className="text-sm sm:text-base font-black text-white">
                      {c.numero}
                    </span>
                  </div>
                )}
              </div>
              <figcaption className="p-3 sm:p-4">
                <div className="text-[11px] sm:text-xs font-black uppercase tracking-widest text-red-500 mb-1">
                  {c.titre}
                </div>
                <p className="text-[12px] sm:text-[13px] text-foreground/80 leading-snug">
                  {c.description}
                </p>
              </figcaption>
            </figure>
            )
          ))}
        </div>
      )}

      {section.compareCards && (
        <div className="grid md:grid-cols-2 gap-4">
          {section.compareCards.map((c, i) => (
            <div key={i} className={`mc-card ${c.accent === '#16a34a' ? 'mc-card-accent-success' : 'mc-card-accent-exam'} p-5 sm:p-6`}>
              <div
                className="text-[10px] font-black uppercase tracking-[0.16em] mb-2"
                style={{ color: c.accent }}
              >
                {c.kicker}
              </div>
              <h3 className="text-xl sm:text-2xl font-black tracking-tight mb-3">{c.title}</h3>
              {c.intro && (
                <p
                  className="text-sm text-foreground/80 mb-4 italic"
                  dangerouslySetInnerHTML={{ __html: c.intro }}
                />
              )}
              <ul className="space-y-2">
                {c.points.map((p, j) => (
                  <CheckRow key={j} ok={p.ok}>{p.text}</CheckRow>
                ))}
              </ul>
              {c.footer && (
                <div className="mt-4 p-3 rounded-lg text-xs italic" style={{ background: `${c.accent}14`, color: c.accent === '#16a34a' ? '#15803d' : '#7f1d1d' }}>
                  <strong className="not-italic uppercase tracking-widest text-[10px] block mb-1">À retenir</strong>
                  {c.footer}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {section.checkLists && (
        <div className="grid sm:grid-cols-2 gap-3">
          {section.checkLists.map((cl, i) => (
            <div key={i} className="mc-card p-4">
              <div
                className="text-[10px] font-black uppercase tracking-[0.16em] mb-2"
                style={{ color: cl.accent }}
              >
                {cl.kicker}
              </div>
              <ul className="space-y-1.5">
                {cl.items.map((item, j) => (
                  <CheckRow key={j} ok={item.ok}>{item.text}</CheckRow>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {section.callouts?.map((c, i) => {
        const Icon = c.icon || Lightbulb;
        const accent = c.accent || '#1d4ed8';
        return (
          <div
            key={i}
            className="mc-card mc-card-accent-official p-4 flex items-start gap-3"
          >
            <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: accent }} />
            <div
              className="text-sm text-foreground/85 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: c.body }}
            />
          </div>
        );
      })}

      {section.normCards && (
        <div className="grid sm:grid-cols-3 gap-3">
          {section.normCards.map((n, i) => (
            <div key={i} className="mc-card p-4 sm:p-5">
              <div
                className="inline-flex items-center justify-center px-3 py-1.5 rounded-md text-white font-mono font-black text-base mb-3"
                style={{ background: n.color }}
              >
                {n.code}
              </div>
              <h3 className="font-black text-sm mb-2">{n.title}</h3>
              <p
                className="text-xs text-foreground/75 leading-snug"
                dangerouslySetInnerHTML={{ __html: n.body }}
              />
            </div>
          ))}
        </div>
      )}

      {section.pullQuote && (
        <PullQuote source={section.pullQuote.source}>{section.pullQuote.text}</PullQuote>
      )}
    </section>
  );
};

// ---------- Quiz ----------
const Quiz = ({ quiz }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const score = useMemo(() => {
    if (!submitted) return 0;
    return quiz.reduce((acc, q) => acc + (answers[q.id] === q.correctIndex ? 1 : 0), 0);
  }, [submitted, answers, quiz]);

  const allAnswered = quiz.every((q) => typeof answers[q.id] === 'number');
  const navigate = useNavigate();

  const handleAnswer = (qid, idx) => {
    if (submitted) return;
    setAnswers((p) => ({ ...p, [qid]: idx }));
  };

  const handleSubmit = () => {
    if (!allAnswered) return;
    setSubmitted(true);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="quiz-result"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleReset = () => {
    setAnswers({});
    setSubmitted(false);
  };

  return (
    <section
      className="mc-card p-5 sm:p-7 border-l-4 border-l-[hsl(var(--primary))] space-y-5"
      data-testid="article-quiz"
    >
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] flex-shrink-0">
          <Award className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.16em] text-[hsl(var(--primary))]">
            Mini-quiz d'ancrage — {quiz.length} questions
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Vérifiez vos acquis
          </h2>
          <p className="text-sm text-foreground/70 mt-1">
            Vérifiez en moins de 2 minutes que vous avez tout retenu.
          </p>
        </div>
      </header>

      <div className="space-y-5">
        {quiz.map((q, qi) => {
          const userAnswer = answers[q.id];
          const isCorrect = submitted && userAnswer === q.correctIndex;
          return (
            <div key={q.id} className="space-y-3" data-testid={`quiz-q-${q.id}`}>
              <div className="flex items-start gap-2.5">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] font-black text-xs flex-shrink-0">
                  {qi + 1}
                </span>
                <p className="font-black text-sm sm:text-base leading-snug pt-0.5">{q.question}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 sm:pl-9">
                {q.options.map((opt, oi) => {
                  const selected = userAnswer === oi;
                  const correct = submitted && oi === q.correctIndex;
                  const wrong = submitted && selected && oi !== q.correctIndex;
                  return (
                    <button
                      key={oi}
                      onClick={() => handleAnswer(q.id, oi)}
                      disabled={submitted}
                      data-testid={`quiz-${q.id}-opt-${oi}`}
                      className={[
                        'text-left px-3 py-2.5 rounded-xl border text-sm font-medium transition-all flex items-start gap-2',
                        submitted
                          ? correct
                            ? 'border-[#16a34a] bg-[#16a34a]/10 text-foreground'
                            : wrong
                              ? 'border-[#cf2031] bg-[#cf2031]/10 text-foreground'
                              : 'border-border bg-card text-foreground/70'
                          : selected
                            ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/8 text-foreground'
                            : 'border-border bg-card hover:border-[hsl(var(--primary))]/50 hover:bg-[hsl(var(--primary))]/5',
                      ].join(' ')}
                    >
                      <span className="font-mono font-black text-xs mt-0.5 opacity-70">
                        {String.fromCharCode(65 + oi)}.
                      </span>
                      <span className="flex-1">{opt}</span>
                      {submitted && correct && <CheckCircle2 className="w-4 h-4 text-[#16a34a] flex-shrink-0" />}
                      {submitted && wrong && <XCircle className="w-4 h-4 text-[#cf2031] flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {submitted && (
                <div
                  className={[
                    'sm:ml-9 rounded-xl p-3 text-xs leading-relaxed flex items-start gap-2',
                    isCorrect
                      ? 'bg-[#16a34a]/8 text-foreground border border-[#16a34a]/20'
                      : 'bg-[#cf2031]/8 text-foreground border border-[#cf2031]/20',
                  ].join(' ')}
                  data-testid={`quiz-${q.id}-explain`}
                >
                  <Lightbulb className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>{isCorrect ? 'Bonne réponse !' : 'Réponse correcte : '}</strong>
                    {!isCorrect && (
                      <strong>
                        {String.fromCharCode(65 + q.correctIndex)}. {q.options[q.correctIndex]}.
                      </strong>
                    )}
                    <span className="block mt-1 text-foreground/85">{q.explain}</span>
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!submitted ? (
        <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
          <p className="text-xs text-foreground/65 italic">
            {Object.keys(answers).length} / {quiz.length} questions répondues
          </p>
          <Button onClick={handleSubmit} disabled={!allAnswered} className="mc-btn-primary" data-testid="quiz-submit">
            Valider mes réponses <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      ) : (
        <div
          className="rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-[hsl(var(--primary))]/10 to-transparent border border-[hsl(var(--primary))]/30"
          data-testid="quiz-result"
        >
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center flex-shrink-0">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.16em] text-[hsl(var(--primary))]">
                  Résultat
                </div>
                <div
                  className="text-2xl sm:text-3xl font-black tracking-tight"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {score} / {quiz.length}
                </div>
                <p className="text-sm text-foreground/80 mt-1">
                  {score === quiz.length && '🏆 Parfait ! Tu maîtrises le sujet.'}
                  {score === quiz.length - 1 && 'Excellent — relis juste l\'erreur pour la mémoriser.'}
                  {score >= 3 && score < quiz.length - 1 && 'Bonne base — un dernier passage sur l\'article et c\'est validé.'}
                  {score < 3 && 'Reprends les sections clés et relance le quiz.'}
                </p>
              </div>
            </div>
            <div className="ml-auto flex gap-2 flex-wrap">
              <Button onClick={handleReset} variant="outline" size="sm" data-testid="quiz-reset">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refaire le quiz
              </Button>
              <Button onClick={() => navigate('/revision')} className="mc-btn-primary" size="sm" data-testid="quiz-cta-revision">
                <Target className="w-3.5 h-3.5 mr-1.5" /> Continuer ma révision
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ---------- Composant principal ----------
export default function MagazineArticleTemplate({ article, pathOverride }) {
  const navigate = useNavigate();

  // Path canonique :
  //  - Par défaut : /magazine/<slug>
  //  - Si la page est servie via une URL SEO courte (ex. /casque-moto-ece-22-06),
  //    on utilise pathOverride pour que le canonical pointe vers la version courte.
  const canonicalPath = pathOverride || `/magazine/${article.slug}`;

  // Si l'article a un quiz pédagogique → on dérive automatiquement une FAQPage
  // schema (chaque question du quiz devient une Q/R officielle Google).
  // Très utile pour les rich snippets "People also ask".
  const faqFromQuiz = useMemo(() => {
    if (!article.quiz?.length) return null;
    return article.quiz.map((q) => {
      const correct = q.options?.[q.correctIndex];
      const answer = correct
        ? `${correct}. ${q.explain || ''}`.trim()
        : (q.explain || '').trim();
      return { q: q.question, a: answer };
    });
  }, [article.quiz]);

  // FAQ explicite (si l'article en définit une via `faq: [{q, a}]`)
  const faqExplicit = article.faq?.length ? article.faq : null;
  const faqItems = faqExplicit || faqFromQuiz;

  useSeo({
    title: `${article.title} — ${article.kicker || 'Conseil ECSR'}`,
    description: article.seoDescription || article.subtitle,
    keywords: article.seoKeywords || '',
    path: canonicalPath,
    // coverPoster (image éditoriale annotée) prioritaire pour le partage social
    // og:image / twitter:image ; heroImage sert au preload LCP visible sur la page.
    image: article.coverPoster || article.heroImage,
    preloadImage: article.heroImage,
    jsonLd: combineJsonLd(
      buildBreadcrumb([
        ['Magazine', '/magazine'],
        [article.title, canonicalPath],
      ]),
      buildArticleSchema({
        headline: article.title,
        description: article.seoDescription || article.subtitle,
        path: canonicalPath,
        image: article.coverPoster || article.heroImage,
        datePublished: article.datePublished,
        dateModified: article.dateModified || article.datePublished,
        author: article.author || 'Équipe CODE 2 ROUES — Moniteurs ECSR',
        section: article.category,
      }),
      buildLearningResourceSchema({
        name: article.title,
        description: article.seoDescription || article.subtitle,
        path: canonicalPath,
        learningResourceType: 'Article',
        about: article.about || 'Magazine pédagogique moto — CODE 2 ROUES',
      }),
      faqItems ? buildFaqSchema(faqItems) : null,
    ),
  });

  return (
    <article className="space-y-8 sm:space-y-12" data-testid={`magazine-article-${article.slug}`}>
      {/* Fil d'Ariane */}
      <nav className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium" aria-label="Fil d'Ariane">
        <button onClick={() => navigate('/')} className="text-foreground/60 hover:text-foreground transition-colors">
          Accueil
        </button>
        <span className="text-foreground/30">/</span>
        <button onClick={() => navigate('/magazine')} className="text-foreground/60 hover:text-foreground transition-colors">
          Magazine
        </button>
        <span className="text-foreground/30">/</span>
        <span className="text-foreground font-bold">{article.shortTitle || article.title}</span>
      </nav>

      {/* HERO IMAGE — image éditoriale seule, sans texte par-dessus.
          Le titre, le kicker, le sous-titre et les badges sont déplacés
          dans le bloc HEADER juste en dessous pour un rendu magazine plus
          aéré et plus lisible (pas de doublon avec le chapô). */}
      <section
        className="relative overflow-hidden rounded-sm editorial-border-double"
        data-testid="article-hero"
      >
        <img
          src={article.heroImage}
          alt={article.heroImageAlt || article.title}
          className="block w-full h-[240px] sm:h-[420px] lg:h-[500px] object-cover"
          loading="eager"
          style={{ filter: 'saturate(0.95) contrast(1.03)' }}
        />
        {/* Très léger vignettage bas pour la profondeur, sans masquer l'image */}
        <div
          className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(15,12,8,0) 0%, rgba(15,12,8,0.35) 100%)',
          }}
        />
        {/* Légende discrète en bas à gauche : crédit / catégorie (desktop only) */}
        {article.topRightKicker && (
          <div className="hidden sm:flex absolute bottom-4 left-6 items-center gap-2 z-10">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
            <span className="editorial-kicker text-[10px] text-white/85 uppercase tracking-[0.20em] font-black">
              {article.topRightKicker.line1} · {article.topRightKicker.line2}
            </span>
          </div>
        )}
        {/* Crédit photo discret en bas à droite (auteur/source) */}
        {article.imageCredit && (
          <div
            className="absolute bottom-2 right-3 z-10 px-2 py-0.5 rounded bg-black/40 backdrop-blur-sm"
            data-testid="article-image-credit"
          >
            <span className="text-[9px] sm:text-[10px] text-white/80 font-medium tracking-wide">
              © {article.imageCredit}
            </span>
          </div>
        )}
      </section>

      {/* HEADER ÉDITORIAL — kicker, titre, sous-titre, badges (sous l'image) */}
      <header className="max-w-4xl" data-testid="article-header">
        {article.kicker && (
          <div className="editorial-tag editorial-tag-red mb-4">
            <ShieldCheck className="w-3 h-3" />
            <span>{article.kicker}</span>
          </div>
        )}
        <h1
          className="text-[2rem] sm:text-5xl lg:text-6xl font-extrabold leading-[1.08] sm:leading-[1.02] text-foreground tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          data-testid="article-title"
        >
          {article.titleJsx || article.title}
        </h1>
        {article.subtitle && (
          <p
            className="text-base sm:text-xl text-foreground/80 mt-4 sm:mt-5 max-w-2xl leading-relaxed italic"
            style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
            dangerouslySetInnerHTML={{ __html: article.subtitle }}
          />
        )}

        <div className="flex flex-wrap items-center gap-2 mt-5 sm:mt-6">
          <OfficialBadge size="md" />
          {article.source?.label && (
            <span className="editorial-tag editorial-tag-outline text-foreground/75 border-border">
              <FileText className="w-3 h-3" />
              <span>Source : {article.source.label}</span>
            </span>
          )}
        </div>
      </header>

      {/* CHAPÔ */}
      {article.chapo && (
        <section className="grid md:grid-cols-3 gap-4" data-reveal="">
          <div className="md:col-span-2 mc-card p-5 sm:p-7">
            {article.chapo.kicker && (
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-2">
                {article.chapo.kicker}
              </div>
            )}
            <p
              className="text-base sm:text-lg leading-relaxed text-foreground/90"
              dangerouslySetInnerHTML={{ __html: article.chapo.body }}
            />
          </div>
          {article.chapo.sideStat && (
            <div className={`mc-card p-5 sm:p-6 ${article.chapo.sideStat.accent === '#16a34a' ? 'mc-card-accent-success' : 'mc-card-accent-exam'}`}>
              <div
                className="text-[10px] font-black uppercase tracking-[0.18em] mb-2"
                style={{ color: article.chapo.sideStat.accent || '#cf2031' }}
              >
                {article.chapo.sideStat.kicker}
              </div>
              <div className="text-4xl sm:text-5xl font-black text-foreground tracking-tight leading-none">
                {article.chapo.sideStat.big}
              </div>
              <p
                className="text-xs text-foreground/75 mt-2 leading-snug"
                dangerouslySetInnerHTML={{ __html: article.chapo.sideStat.body }}
              />
            </div>
          )}
        </section>
      )}

      {/* PULL QUOTE */}
      {article.pullQuote && (
        <PullQuote source={article.pullQuote.source}>{article.pullQuote.text}</PullQuote>
      )}

      {/* SECTIONS */}
      {article.sections?.map((section, idx) => (
        <Section key={idx} section={section} idx={idx} />
      ))}

      {/* QUIZ
          Pivot magazine 2026 : les QCM intra-article sont désactivés sur le site
          grand public élève (format purement éditorial). Code conservé pour
          rollback éventuel ou usage en salle enseignant (/enseignant/salle). */}
      {false && article.quiz && article.quiz.length > 0 && <Quiz quiz={article.quiz} />}

      {/* FUNNEL PÉDAGOGIQUE — Magazine → QCM thématique
          Pivot magazine 2026 : désactivé sur le site grand public. Le funnel
          mini-QCM contextuel n'apparaît plus sur les articles élève. Code
          conservé pour rollback ou usage interne enseignant. */}
      {false && (() => {
        const etmMapping = getEtmMapping(article);
        if (!etmMapping) return null;
        return <EtmQuizFunnel mapping={etmMapping} articleSlug={article.slug} />;
      })()}

      {/* POSTER — Récap visuel téléchargeable (série 5 étapes) */}
      {(article.posterVertical || article.posterHorizontal) && (
        <PosterShareBlock article={article} />
      )}

      {/* CTA FINAL */}
      {article.ctaFinal && (
        <section
          className="mc-card mc-card-accent-official p-5 sm:p-7 flex items-start gap-4 flex-wrap"
          data-reveal=""
        >
          <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-[260px]">
            {article.ctaFinal.kicker && (
              <div className="text-[10px] font-black uppercase tracking-[0.16em] text-[hsl(var(--primary))] mb-1">
                {article.ctaFinal.kicker}
              </div>
            )}
            <h2
              className="text-xl sm:text-2xl font-black tracking-tight mb-2"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {article.ctaFinal.title}
            </h2>
            <p
              className="text-sm text-foreground/80 mb-3 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.ctaFinal.body }}
            />
            <div className="flex flex-wrap gap-2">
              {article.ctaFinal.primary && (
                <Button onClick={() => navigate(article.ctaFinal.primary.to)} className="mc-btn-primary">
                  {article.ctaFinal.primary.label} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              )}
              {article.ctaFinal.secondary && (
                <Button onClick={() => navigate(article.ctaFinal.secondary.to)} variant="outline">
                  {article.ctaFinal.secondary.label}
                </Button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* SOURCE */}
      {article.source && (
        <footer className="mc-card p-4 text-xs text-foreground/65 italic flex items-start gap-2">
          <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <p>
            <strong className="not-italic">Source officielle :</strong>{' '}
            {article.source.label}
            {article.source.url && (
              <>
                {' ('}
                <a
                  href={article.source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  {article.source.url.replace(/^https?:\/\//, '')}
                </a>
                {')'}
              </>
            )}
            {article.source.refs && (
              <>
                {' — '}
                {article.source.refs}
              </>
            )}
          </p>
        </footer>
      )}

      {/* Retour */}
      <div className="pt-2 flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/magazine')} className="text-foreground/70">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour au magazine
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-foreground/70">
          Accueil
        </Button>
      </div>
    </article>
  );
}

export {
  PullQuote,
  InfoCard,
  CheckRow,
  Section,
  Quiz,
};

/* ==========================================================================
   POSTER SHARE BLOCK — Récap visuel téléchargeable + bouton Copier le lien
   Format horizontal (desktop) / vertical (mobile) + partage rapide
   ========================================================================== */
function PosterShareBlock({ article }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const url = typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : '';
    try {
      if (navigator.share) {
        // Sur mobile, préférer le partage natif (WhatsApp, Messages, etc.)
        try {
          await navigator.share({
            title: article.title,
            text: article.subtitle?.replace(/<[^>]+>/g, '') || article.title,
            url,
          });
          return;
        } catch {
          // Utilisateur a annulé — on tombe sur le copier classique
        }
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      // Fallback ultime : prompt manuel
      window.prompt('Copiez ce lien :', url);
    }
  };

  return (
    <section
      className="mc-card p-5 sm:p-7"
      data-reveal=""
      data-testid="article-poster-vertical"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.10) 100%)',
        borderTop: '2px solid rgba(207, 32, 49, 0.35)',
      }}
    >
      <div className="text-center mb-4">
        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-500 mb-2">
          Récap visuel · À télécharger et partager
        </p>
        <h3
          className="text-xl sm:text-2xl font-bold text-foreground mb-1"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Le poster CODE 2 ROUES
        </h3>
        <p className="text-sm text-foreground/70 leading-relaxed max-w-xl mx-auto">
          Format <strong>horizontal</strong> pour l&apos;affichage web et LinkedIn, format <strong>vertical</strong> pour Pinterest, Instagram Stories et l&apos;impression.
        </p>
      </div>
      <div className="flex flex-col items-center gap-4">
        {/* Desktop: version horizontale (ratio 2:1, idéal Open Graph) */}
        {article.posterHorizontal && (
          <img
            src={article.posterHorizontal}
            alt={article.coverPosterAlt || `Poster horizontal ${article.title}`}
            loading="lazy"
            className="hidden md:block w-full max-w-3xl rounded-xl border border-white/[0.08] shadow-2xl"
            data-testid="article-poster-horizontal-img"
          />
        )}
        {/* Mobile: version verticale (portrait, format story/pinterest) */}
        {article.posterVertical && (
          <img
            src={article.posterVertical}
            alt={article.posterVerticalAlt || `Poster vertical ${article.title}`}
            loading="lazy"
            className={`${article.posterHorizontal ? 'md:hidden' : ''} w-full max-w-xs sm:max-w-sm rounded-xl border border-white/[0.08] shadow-2xl`}
            data-testid="article-poster-vertical-img"
          />
        )}
        <div className="flex flex-wrap items-center justify-center gap-2">
          {article.posterHorizontal && (
            <a
              href={article.posterHorizontal}
              download
              className="hidden md:inline-flex items-center gap-2 px-5 h-11 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[13px] font-bold shadow-md transition"
              data-testid="btn-download-poster-horizontal"
              aria-label="Télécharger le poster horizontal (web / LinkedIn)"
            >
              <Download className="w-4 h-4" /> Télécharger — horizontal
            </a>
          )}
          {article.posterVertical && (
            <a
              href={article.posterVertical}
              download
              className={`${article.posterHorizontal ? 'md:hidden' : ''} inline-flex items-center gap-2 px-5 h-11 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[13px] font-bold shadow-md transition`}
              data-testid="btn-download-poster"
              aria-label="Télécharger le poster vertical (Pinterest / Instagram Stories)"
            >
              <Download className="w-4 h-4" /> Télécharger le poster
            </a>
          )}
          <button
            type="button"
            onClick={handleCopy}
            className={`inline-flex items-center gap-2 px-5 h-11 rounded-lg border text-[13px] font-bold shadow-sm transition ${
              copied
                ? 'bg-green-600/90 border-green-500 text-white'
                : 'bg-card border-border/50 hover:border-[hsl(var(--primary))]/60 text-foreground/85 hover:text-foreground'
            }`}
            data-testid="btn-copy-link"
            aria-label={copied ? 'Lien copié dans le presse-papiers' : 'Copier le lien de cet article'}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" /> Lien copié !
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4" /> Copier le lien
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}
